import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { CodeLanguages, EditorModeByLanguage } from 'js/Constants';
import { updateFormData } from 'js/actions';
import { FORM_TYPE_CREATE_REUSABLE_TX } from 'js/constants/formTypes';
import { TransformType } from 'new/components/ReusableTransform/types';

import { getCodeLanguageFormData } from './utils';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';

export const useSelectedEditorLanguage = (
  language: string,
  transformType: TransformType,
  isCapsuleCluster = false,
) => {
  const dispatch = useDispatch();
  const isPython3Enabled = useFeatureFlag('UI_ENABLE_PYTHON3');

  const current = EditorModeByLanguage[language]?.name;

  const isPythonCode = current === EditorModeByLanguage[CodeLanguages.PYTHON].name;
  const isPython3Code = current === EditorModeByLanguage[CodeLanguages.PYTHON3].name;
  const isJavascriptCode = current === EditorModeByLanguage[CodeLanguages.JAVASCRIPT].name;
  const isNexlaJSON = current === EditorModeByLanguage[CodeLanguages.JOLT].name;

  const changeCodeLanguageSelection = (language: string) => {
    const formData = getCodeLanguageFormData(transformType, language);
    dispatch(updateFormData(FORM_TYPE_CREATE_REUSABLE_TX, formData));
  };

  const switchToJS = () => changeCodeLanguageSelection(CodeLanguages.JAVASCRIPT);
  const switchToNexlaJSON = () => changeCodeLanguageSelection(CodeLanguages.JOLT);
  const switchToPythonLegacy = () => changeCodeLanguageSelection(CodeLanguages.PYTHON);
  const switchToPython3 = () => changeCodeLanguageSelection(CodeLanguages.PYTHON3);

  const options = useMemo(
    () => [
      {
        label: 'Python',
        value: 'python',
        selected: isPythonCode,
        onClick: switchToPythonLegacy,
      },
      {
        label: 'Python 3 (Experimental)',
        value: 'python3',
        selected: isPython3Code,
        hidden: !isPython3Enabled || isCapsuleCluster,
        onClick: switchToPython3,
      },
      {
        label: 'JavaScript',
        value: 'javascript',
        selected: isJavascriptCode,
        onClick: switchToJS,
      },
      {
        label: 'Nexla JSON',
        value: 'nexla_json',
        selected: isNexlaJSON,
        hidden: transformType !== TransformType.RECORD,
        onClick: switchToNexlaJSON,
      },
    ],
    [
      isJavascriptCode,
      isNexlaJSON,
      transformType,
      isPython3Enabled,
      isPython3Code,
      isPythonCode,
      isCapsuleCluster,
    ],
  );

  return { options };
};
